import './style.css';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { detectMode, isEmbedMode, WORLD_META, getSiteBase, getApiBase, dimensionToWorld } from './config';
import { loadDynmapConfig, getWorld, getFlatMap } from './dynmapConfig';
import { createHdProjection, createDynmapCrs } from './dynmapCrs';
import { createDynmapTileLayer } from './tileLayer';
import { parseLocationFromUrl, writeLocationHash, classicDynmapUrl } from './deepLink';
import { loadPhotoPoints } from './photoApi';
import { createPhotoLayer, type PhotoLayerHandle } from './photoLayer';
import type { DynmapConfig, FocusState, McLocation, PhotoPoint } from './types';

const mode = detectMode();
const embed = isEmbedMode();
const brandTitle = document.getElementById('brand-title')!;
const modeBadge = document.getElementById('mode-badge')!;
const worldSwitch = document.getElementById('world-switch')!;
const focusCard = document.getElementById('focus-card')!;
const focusWorld = document.getElementById('focus-world')!;
const focusCoords = document.getElementById('focus-coords')!;
const focusTitle = document.getElementById('focus-title')!;
const focusShotLink = document.getElementById('focus-shot-link') as HTMLAnchorElement;
const focusOpenClassic = document.getElementById('focus-open-classic') as HTMLAnchorElement;
const btnRecenter = document.getElementById('btn-recenter')!;
const topbar = document.querySelector('.topbar') as HTMLElement | null;

brandTitle.textContent = mode === 'photos' ? 'Photos Map' : 'ReBorned Map';
document.title = mode === 'photos' ? 'Photos Map — ReBorned.One' : 'Карта — ReBorned.One';
modeBadge.textContent = mode === 'photos' ? 'Все скриншоты' : 'Место';

if (embed) {
  document.body.classList.add('is-embed');
  if (topbar) topbar.hidden = true;
  modeBadge.hidden = true;
  focusCard.hidden = true;
  document.title = 'Место на карте';
}

let map: L.Map;
let cfg: DynmapConfig;
let currentWorld = 'world';
let tileLayer: L.TileLayer | null = null;
let focusMarker: L.Marker | null = null;
let photoLayer: PhotoLayerHandle | null = null;
let photoPoints: PhotoPoint[] = [];
let focus: FocusState = {
  location: null,
  shotId: null,
  title: null,
  image: null,
  player: null,
};

async function main() {
  cfg = await loadDynmapConfig();
  const parsed = parseLocationFromUrl();
  if (parsed.location) {
    focus.location = parsed.location;
    currentWorld = parsed.location.world;
  } else {
    currentWorld = cfg.worlds[0]?.name || 'world';
  }
  focus.shotId = parsed.shotId;

  if (focus.shotId) {
    await hydrateShotMeta(focus.shotId);
  }

  map = L.map('map', {
    crs: createDynmapCrs(),
    zoomControl: !embed,
    attributionControl: !embed,
    minZoom: 0,
    zoomSnap: 1,
    zoomDelta: 1,
    // No fade — tiles appear the moment they load (less black-hole feel)
    fadeAnimation: false,
    preferCanvas: false,
  });

  if (!embed) {
    renderWorldSwitch();
  }

  // Photos: load points before first view so we can fitBounds to ALL of them
  if (mode === 'photos' && !embed) {
    photoPoints = await loadPhotoPoints();
  }

  await switchWorld(currentWorld, { preserveView: false });

  if (mode === 'photos' && !embed) {
    updatePhotosBadge();
  }

  // Place/embed: focus pin. Photos without deep-link: already fit in switchWorld.
  if (mode !== 'photos' || focus.location || focus.shotId) {
    applyFocus(focus.location, { fly: !embed && mode !== 'photos' });
  }

  // Leaflet often measures 0×0 on first paint (fonts/topbar) — reflow tiles
  const kickSize = () => {
    map.invalidateSize(false);
    if (mode === 'photos' && !focus.location && !focus.shotId) {
      fitPhotosInWorld(currentWorld, false);
    } else if (focus.location) {
      // re-assert view after size is known
      const proj = (map as L.Map & { __proj?: ReturnType<typeof createHdProjection> }).__proj;
      if (proj) {
        const ll = proj.locationToLatLng(
          focus.location.x,
          focus.location.y,
          focus.location.z,
        );
        map.setView(ll, map.getZoom(), { animate: false });
      }
    }
  };
  window.setTimeout(kickSize, 50);
  window.setTimeout(kickSize, 250);

  if (embed) {
    window.setTimeout(() => {
      map.invalidateSize();
      if (focus.location) applyFocus(focus.location, { fly: false });
    }, 80);
  }

  btnRecenter.addEventListener('click', () => {
    if (mode === 'photos' && !focus.shotId) {
      fitPhotosInWorld(currentWorld, true);
      return;
    }
    if (focus.location) {
      applyFocus(focus.location, { fly: true });
    } else {
      const w = getWorld(cfg, currentWorld);
      const c = w?.center || { x: 0, y: 64, z: 0 };
      applyFocus(
        { world: currentWorld, x: c.x, y: c.y, z: c.z, mapName: 'flat', zoom: 5 },
        { fly: true },
      );
    }
  });

  window.addEventListener('hashchange', () => {
    const next = parseLocationFromUrl();
    if (next.location) {
      focus.location = next.location;
      if (next.location.world !== currentWorld) {
        void switchWorld(next.location.world, { preserveView: false }).then(() =>
          applyFocus(next.location, { fly: true }),
        );
      } else {
        applyFocus(next.location, { fly: true });
      }
    }
  });
}

function renderWorldSwitch() {
  const worlds = [...(cfg.worlds || [])].sort(
    (a, b) => (WORLD_META[a.name]?.order ?? 9) - (WORLD_META[b.name]?.order ?? 9),
  );
  worldSwitch.innerHTML = '';
  for (const w of worlds) {
    const meta = WORLD_META[w.name] || {
      label: w.title || w.name,
      icon: WORLD_META.world.icon,
    };
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `world-btn${w.name === currentWorld ? ' is-active' : ''}`;
    btn.dataset.world = w.name;
    btn.innerHTML = `<img src="${meta.icon}" alt="" /><span>${meta.label}</span>`;
    btn.addEventListener('click', () => {
      void switchWorld(w.name, { preserveView: false });
    });
    worldSwitch.appendChild(btn);
  }
}

async function switchWorld(worldName: string, opts: { preserveView: boolean }) {
  const world = getWorld(cfg, worldName);
  if (!world) return;
  const mapDef = getFlatMap(world);
  if (!mapDef) return;

  currentWorld = worldName;
  worldSwitch.querySelectorAll('.world-btn').forEach((el) => {
    el.classList.toggle('is-active', (el as HTMLElement).dataset.world === worldName);
  });

  const proj = createHdProjection(mapDef);
  if (tileLayer) {
    map.removeLayer(tileLayer);
  }
  tileLayer = createDynmapTileLayer(worldName, proj);
  tileLayer.addTo(map);
  map.setMaxZoom(proj.maxZoom);
  map.options.maxZoom = proj.maxZoom;

  // Store proj on map for other layers
  (map as L.Map & { __proj?: typeof proj }).__proj = proj;

  if (photoLayer) {
    photoLayer.destroy();
    photoLayer = null;
  }
  if (mode === 'photos' && photoPoints.length) {
    attachPhotoLayer();
  }

  if (!opts.preserveView) {
    // Photos map: frame ALL geo shots in this world (spawn + far bases)
    if (mode === 'photos' && !embed && !(focus.location && focus.location.world === worldName)) {
      clearFocusMarker();
      focusCard.hidden = true;
      if (!fitPhotosInWorld(worldName, false)) {
        // no photos here — fall back to world center, no pin spam
        const c = world.center || { x: 0, y: 64, z: 0 };
        const p = createHdProjection(mapDef);
        const ll = p.locationToLatLng(c.x, c.y, c.z);
        map.setView(ll, Math.min(3, p.maxNativeZoom), { animate: false });
      }
      updatePhotosBadge();
      return;
    }

    const loc =
      focus.location && focus.location.world === worldName
        ? focus.location
        : {
            world: worldName,
            x: world.center?.x ?? 0,
            y: world.center?.y ?? 64,
            z: world.center?.z ?? 0,
            mapName: 'flat',
            zoom: 5,
          };
    applyFocus(loc, { fly: false });
  }
}

function attachPhotoLayer() {
  const proj = (map as L.Map & { __proj?: ReturnType<typeof createHdProjection> }).__proj;
  if (!proj) return;
  photoLayer = createPhotoLayer(map, proj, photoPoints, currentWorld);
  updatePhotosBadge();
}

function updatePhotosBadge() {
  if (mode !== 'photos') return;
  const onWorld = photoPoints.filter((p) => p.world === currentWorld).length;
  const total = photoPoints.length;
  modeBadge.textContent =
    total === 0
      ? 'Скрины · 0'
      : onWorld === total
        ? `Скрины · ${total}`
        : `Скрины · ${onWorld}/${total}`;
}

/**
 * Fit viewport to photos in this world.
 * If points are very far apart (e.g. spawn + 5km base), fitBounds would
 * zoom to 0 and request a huge cold tile grid — prefer the densest cluster
 * so the map stays usable; far pins remain on the layer to pan to.
 */
function fitPhotosInWorld(worldName: string, fly: boolean): boolean {
  const proj = (map as L.Map & { __proj?: ReturnType<typeof createHdProjection> }).__proj;
  if (!proj) return false;
  const pts = photoPoints.filter((p) => p.world === worldName);
  if (!pts.length) return false;

  map.invalidateSize(false);

  const lls = pts.map((p) => proj.locationToLatLng(p.x, p.y, p.z));
  const go = (ll: L.LatLng, z: number) => {
    const zoom = Math.min(proj.maxZoom, Math.max(0, z));
    if (fly) map.flyTo(ll, zoom, { duration: 0.65 });
    else map.setView(ll, zoom, { animate: false });
  };

  if (pts.length === 1) {
    go(lls[0], proj.maxNativeZoom);
    return true;
  }

  const bounds = L.latLngBounds(lls);
  const spanLng = Math.abs(bounds.getEast() - bounds.getWest());
  const spanLat = Math.abs(bounds.getNorth() - bounds.getSouth());
  // CRS.Simple: ~span in map units. > ~80 ≈ multi-km in flat scale-4 worlds.
  const huge = spanLng > 80 || spanLat > 80;

  if (huge) {
    // Center on most recent point (API returns newest first) at a sharp zoom
    go(lls[0], Math.min(proj.maxNativeZoom, 4));
    return true;
  }

  const padded = bounds.pad(0.3);
  const opts: L.FitBoundsOptions = {
    animate: fly,
    maxZoom: Math.min(proj.maxNativeZoom, 5),
    padding: [56, 56],
  };
  if (fly) map.flyToBounds(padded, { ...opts, duration: 0.65 });
  else map.fitBounds(padded, opts);
  return true;
}

function clearFocusMarker() {
  if (focusMarker) {
    map.removeLayer(focusMarker);
    focusMarker = null;
  }
}

function applyFocus(loc: McLocation | null, opts: { fly: boolean }) {
  const proj = (map as L.Map & { __proj?: ReturnType<typeof createHdProjection> }).__proj;
  if (!loc || !proj) {
    focusCard.hidden = true;
    if (focusMarker) {
      map.removeLayer(focusMarker);
      focusMarker = null;
    }
    return;
  }

  focus.location = { ...loc, world: loc.world || currentWorld };
  if (focus.location.world !== currentWorld) {
    void switchWorld(focus.location.world, { preserveView: false }).then(() =>
      applyFocus(focus.location, opts),
    );
    return;
  }

  const ll = proj.locationToLatLng(loc.x, loc.y, loc.z);
  // LiveAtlas hash zoom === Leaflet zoom (higher = closer). zoomReverse only
  // affects tile URL math, not the meaning of the number in the URL.
  // Default to maxNativeZoom = sharpest full-res tiles (not soft overzoom).
  const maxZ = proj.maxZoom;
  let leafletZoom =
    loc.zoom != null && Number.isFinite(loc.zoom)
      ? Math.round(loc.zoom)
      : proj.maxNativeZoom;
  leafletZoom = Math.min(maxZ, Math.max(0, leafletZoom));

  if (opts.fly) {
    map.flyTo(ll, leafletZoom, { duration: 0.7 });
  } else {
    map.setView(ll, leafletZoom, { animate: false });
  }

  if (focusMarker) map.removeLayer(focusMarker);
  // Minecraft-style red pixel pin (CSS box-shadow bitmap)
  const icon = L.divIcon({
    className: 'focus-pin-wrap',
    html: `<div class="mc-pin" aria-hidden="true">
      <span class="mc-pin-glow"></span>
      <span class="mc-pin-sprite"></span>
    </div>`,
    iconSize: [24, 36],
    iconAnchor: [12, 34],
  });
  focusMarker = L.marker(ll, { icon, zIndexOffset: 1000, interactive: false }).addTo(map);

  // Full UI chrome only outside embed mode
  if (!embed) {
    const meta = WORLD_META[loc.world] || WORLD_META.world;
    focusWorld.innerHTML = `<span class="world-chip"><img src="${meta.icon}" alt="" /><span>${meta.label}</span></span>`;
    focusCoords.textContent = `X:${Math.round(loc.x)}  Y:${Math.round(loc.y)}  Z:${Math.round(loc.z)}`;
    focusTitle.textContent =
      focus.title ||
      (mode === 'photos' ? 'Точка на карте' : 'Место съёмки');
    if (focus.shotId) {
      focusShotLink.hidden = false;
      focusShotLink.href = `${getSiteBase()}/screenshots.html?shot=${encodeURIComponent(focus.shotId)}`;
    } else {
      focusShotLink.hidden = true;
    }
    focusOpenClassic.href = classicDynmapUrl(focus.location);
    focusCard.hidden = mode === 'photos' && !focus.shotId && !parseLocationFromUrl().location
      ? true
      : false;
    if (mode === 'place') focusCard.hidden = false;
  } else {
    focusCard.hidden = true;
  }

  writeLocationHash({
    ...focus.location,
    zoom: leafletZoom,
  });
}

async function hydrateShotMeta(shotId: string) {
  try {
    const urls = [
      `${getApiBase()}/screenshots/${encodeURIComponent(shotId)}`,
      `${getSiteBase()}/api/screenshots/${encodeURIComponent(shotId)}`,
    ];
    let data: Record<string, unknown> | null = null;
    for (const url of urls) {
      try {
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (res.ok) {
          data = await res.json();
          break;
        }
      } catch {
        /* try next */
      }
    }
    if (!data) return;
    const shot = (data.screenshot || data) as Record<string, unknown>;
    focus.title = String(shot.title || shot.caption || 'Скриншот');
    focus.player = shot.player ? String(shot.player) : null;
    focus.image = shot.image ? String(shot.image) : null;
    const coords = shot.coordinates as { x?: number; y?: number; z?: number } | undefined;
    if (coords && !focus.location) {
      focus.location = {
        world: dimensionToWorld(shot.dimension as string),
        x: Number(coords.x),
        y: Number(coords.y),
        z: Number(coords.z),
        mapName: 'flat',
        zoom: 5,
      };
      currentWorld = focus.location.world;
    }
  } catch {
    /* ignore */
  }
}

void main().catch((err) => {
  console.error(err);
  modeBadge.textContent = 'Ошибка загрузки';
});
